import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation, useNavigate, Link, useParams } from 'react-router-dom';
import './Go.css';
import { mapStyles } from './mapStyles';
import { useAuth } from '../../AuthContext';
import {
  nitrologo,
  economy,
  firstclass,
  luxury,
  motorcycle,
  truck,
  bicycle,
  drivercar,
  locationpickup,
  locationdropoff,
  drunkdriver
} from '../../utilities/images';
import RideProfileDropdown from './RideProfileDropdown';
import { useCart } from './CartContext';
import { db } from '../../firebase';
import Chat from './Chat';
import { ref, onValue, push, set, get, query, orderByChild, equalTo, remove } from "firebase/database";
import PickupItemModal from './PickupItemModal';

const deliveryTypes = ['motorcycle', 'truck', 'bicycle', 'Motorcycle', 'Truck', 'Bicycle'];

const haversineDistanceKm = (coords1, coords2) => {
  if (!coords1 || !coords2) return 0;
  const R = 6371; // Radius of Earth in km
  const dLat = ((coords2.lat - coords1.lat) * Math.PI) / 180;
  const dLng = ((coords2.lng - coords1.lng) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((coords1.lat * Math.PI) / 180) *
      Math.cos((coords2.lat * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const fetchOsrmRoute = async (origin, destination) => {
  if (!origin || !destination) return null;
  try {
    const url = `https://router.project-osrm.org/route/v1/driving/${origin.lng},${origin.lat};${destination.lng},${destination.lat}?overview=full&geometries=geojson`;
    const response = await fetch(url);
    const data = await response.json();
    if (data.code === 'Ok' && data.routes && data.routes.length > 0) {
      const route = data.routes[0];
      const path = route.geometry.coordinates.map(([lng, lat]) => ({ lat, lng }));
      return {
        path,
        distanceMeters: route.distance,
        durationSeconds: route.duration,
        distanceText: `${(route.distance / 1000).toFixed(1)} km`,
        durationText: `${Math.max(1, Math.ceil(route.duration / 60))} min`
      };
    }
  } catch (e) {
    console.warn('OSRM route fetch failed, using Haversine calculation fallback:', e);
  }

  // Fallback calculation using Haversine * 1.25 winding factor
  const straightKm = haversineDistanceKm(origin, destination);
  const estimatedRoadKm = straightKm * 1.25;
  const distanceMeters = estimatedRoadKm * 1000;
  const estimatedMinutes = Math.max(1, Math.ceil((estimatedRoadKm / 30) * 60));
  const durationSeconds = estimatedMinutes * 60;

  return {
    path: [origin, destination],
    distanceMeters,
    durationSeconds,
    distanceText: `${estimatedRoadKm.toFixed(1)} km`,
    durationText: `${estimatedMinutes} min`
  };
};

function Go() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const activeTrip = location.state?.activeTrip;

  const normalizeLocation = (loc) => {
    if (!loc) return null;
    // Already in { address, coords: { lat, lng } } format
    if (loc.coords && typeof loc.coords.lat === 'number' && typeof loc.coords.lng === 'number') {
      return loc;
    }
    // In { address, lat, lng } format from database
    if (typeof loc.lat === 'number' && typeof loc.lng === 'number') {
      return {
        address: loc.address,
        coords: { lat: loc.lat, lng: loc.lng }
      };
    }
    return null; // Invalid format
  };

  const [pickup, setPickup] = useState(() => normalizeLocation(activeTrip?.pickup || location.state?.pickup));
  const [dropoff, setDropoff] = useState(() => normalizeLocation(activeTrip?.destination || location.state?.dropoff));
  const [isLoading, setIsLoading] = useState(!pickup);
  const auth = useAuth();
  const { clearAllCartData } = useCart();
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const profileDropdownRef = useRef(null);
  const driverMarkersRef = useRef({});

  const mapRef = useRef(null);
  const routePolylineRef = useRef(null);
  const assignedDriverMarkerRef = useRef(null);
  const dropoffMarkerRef = useRef(null);
  const pickupMarkerRef = useRef(null);
  const arrivalInfoWindowRef = useRef(null);
  const [map, setMap] = useState(null);
  const [routeInfo, setRouteInfo] = useState(null);
  const [durations, setDurations] = useState({});
  const [rideOptions, setRideOptions] = useState([]);
  const [selectedRide, setSelectedRide] = useState(null);
  const [isPickupItemModalOpen, setIsPickupItemModalOpen] = useState(false);
  const [deliveryDetails, setDeliveryDetails] = useState(null);
  const [tripRequestId, setTripRequestId] = useState(activeTrip?.id || null);
  const [tripStatus, setTripStatus] = useState(activeTrip?.status || null);
  const [tripId, setTripId] = useState(activeTrip?.tripId || null);
  const [assignedDriver, setAssignedDriver] = useState(null);
  const [driverLocation, setDriverLocation] = useState(null);
  const [driverEta, setDriverEta] = useState(null);
  const [deliveryType, setDeliveryType] = useState('instant');
  const [sidebarView, setSidebarView] = useState(() => {
    if (activeTrip) {
      const status = activeTrip.status;
      const driverFoundStatuses = ['found', 'driving_to_pickup', 'on_trip', 'driving_to_destination', 'driver_arrived'];
      if (driverFoundStatuses.includes(status)) {
        return 'driver-found';
      }
      if (status === 'searching') {
        return 'searching';
      }
    }
    return 'ride-options';
  });
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isDriverModalOpen, setIsDriverModalOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [reportReason, setReportReason] = useState('Reckless Driving');
  const [reportComments, setReportComments] = useState('');
  const [isReporting, setIsReporting] = useState(false);
  const [reportSuccess, setReportSuccess] = useState(false);

  const handleSubmitReport = async (e) => {
    e.preventDefault();
    if (!assignedDriver) return;
    setIsReporting(true);
    try {
      const reportsRef = ref(db, 'driverReports');
      const newReportRef = push(reportsRef);
      await set(newReportRef, {
        reportId: newReportRef.key,
        tripId: tripId || tripRequestId || 'unknown',
        driverId: assignedDriver.id || assignedDriver.uid || 'unknown',
        driverName: assignedDriver.name,
        reporterId: auth.currentUser?.uid || 'guest',
        reason: reportReason,
        comments: reportComments,
        timestamp: Date.now()
      });
      setReportSuccess(true);
      setReportComments('');
      setTimeout(() => {
        setReportSuccess(false);
        setIsReportModalOpen(false);
      }, 2500);
    } catch (err) {
      console.error("Error submitting driver report:", err);
      alert("Failed to submit report. Please try again.");
    } finally {
      setIsReporting(false);
    }
  };

  const handleZoomIn = () => {
    if (!map) return;
    const currentZoom = map.getZoom();
    map.setZoom(currentZoom + 1);
  };

  const handleZoomOut = () => {
    if (!map) return;
    const currentZoom = map.getZoom();
    map.setZoom(currentZoom - 1);
  };

  useEffect(() => {
    if (pickup && dropoff) {
      setIsLoading(false);
      return;
    }

    if (params.pickup && params.dropoff && window.google) {
      const geocoder = new window.google.maps.Geocoder();
      const geocodeAddress = async (address) => {
        return new Promise((resolve, reject) => {
          geocoder.geocode({ address }, (results, status) => {
            if (status === 'OK' && results[0]) {
              const location = results[0].geometry.location;
              resolve({ lat: location.lat(), lng: location.lng() });
            } else {
              reject(new Error(`Geocoding failed for "${address}": ${status}`));
            }
          });
        });
      };

      const initializeLocations = async () => {
        try {
          const pickupAddress = decodeURIComponent(params.pickup);
          const dropoffAddress = decodeURIComponent(params.dropoff);

          const [pickupCoords, dropoffCoords] = await Promise.all([
            geocodeAddress(pickupAddress),
            geocodeAddress(dropoffAddress)
          ]);

          setPickup({ address: pickupAddress, coords: pickupCoords });
          setDropoff({ address: dropoffAddress, coords: dropoffCoords });
          setIsLoading(false);
        } catch (error) {
          console.error('Failed to geocode addresses from URL:', error);
          alert('Could not find locations from URL. Redirecting home.');
          navigate('/');
        }
      };

      initializeLocations();
    } else if (!location.state && (!params.pickup || !params.dropoff)) {
      console.error("No location data available.");
      navigate('/'); // Redirect if no location data at all
    }
  }, [params.pickup, params.dropoff, navigate, pickup, dropoff, location.state, activeTrip]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setIsProfileDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [profileDropdownRef]);

  const handleLogout = async () => {
    try {
      setIsProfileDropdownOpen(false);
      await auth.signOut();
      if (clearAllCartData) clearAllCartData();
      localStorage.removeItem('9troride_address');
      localStorage.removeItem('9troride_locationType');
      sessionStorage.removeItem('9troride_checkoutVendorId');
      sessionStorage.removeItem('9troride_checkoutVendorType');
      navigate('/');
    } catch (error) {
      console.error("Failed to log out:", error);
    }
  };

  const handleRideOptionClick = (option) => {
    setSelectedRide(option);
    if (deliveryTypes.includes(option.type)) {
      // If the selected option is a delivery type, open the PickupItemModal.
      setIsPickupItemModalOpen(true);
    } else {
      // Otherwise, if a non-delivery option is selected, ensure the modal is closed.
      setIsPickupItemModalOpen(false);
    }
  };

  const handleConfirmDelivery = (details) => {
    // Callback from PickupItemModal to save delivery details
    setDeliveryDetails({ ...details, deliveryType });
    setIsPickupItemModalOpen(false);
    // Now user can click the main confirm button
  };

  const handleCancelDelivery = () => {
    setIsPickupItemModalOpen(false);
    // Deselect the ride if the modal is cancelled, as delivery details are not confirmed
    setSelectedRide(null); // Deselect ride if modal is cancelled
  };

  const handleCancelSearch = useCallback(async () => {
    setSidebarView('ride-options');
    if (tripRequestId) {
      const tripRef = ref(db, `tripRequests/${tripRequestId}`);
      try {
        await remove(tripRef);
        console.log(`Trip request ${tripRequestId} deleted.`);
      } catch (error) {
        console.error("Failed to delete trip request:", error);
        alert("Failed to cancel the ride request. Please try again.");
      } finally {
        setTripRequestId(null);
        setAssignedDriver(null);
        setDriverLocation(null);
        setTripStatus(null);
      }
    }
  }, [tripRequestId]);

  const handleConfirmRide = async () => {
    if (!selectedRide) {
      alert("Please select a ride option.");
      return;
    }

    const startSearch = async () => {
      if (!auth.currentUser) {
        alert('Please log in to request a ride.');
        navigate('/login');
        return;
      }

      setSidebarView('searching');

      try {
        const riderId = auth.currentUser.uid;
        const tripsRef = ref(db, 'tripRequests');

        // Check for an active trip before creating a new one
        const activeTripQuery = query(tripsRef, orderByChild('riderId'), equalTo(riderId));
        const snapshot = await get(activeTripQuery);

        if (snapshot.exists()) {
          const trips = snapshot.val();
          for (const key in trips) {
            const trip = trips[key];
            const activeStatuses = ['searching', 'found', 'driving_to_pickup', 'on_trip', 'driving_to_destination', 'driver_arrived'];
            if (activeStatuses.includes(trip.status)) {
              alert('You already have an active trip.');
              setSidebarView('ride-options');
              // In a real app, you might restore the active trip UI here.
              return;
            }
          }
        }

        const tripRequest = {
          riderId: riderId,
          pickup: { address: pickup.address, lat: pickup.coords.lat, lng: pickup.coords.lng },
          destination: { address: dropoff.address, lat: dropoff.coords.lat, lng: dropoff.coords.lng },
          estimated: {
            distanceKm: routeInfo.distance.value / 1000,
            durationMin: Math.ceil(routeInfo.duration.value / 60),
            fare: parseFloat(selectedRide.price.replace(/[^0-9.]/g, '')) || 0,
          },
          status: 'searching',
          requestedAt: new Date().toISOString(),
          vehicleClass: selectedRide.type,
          ...(deliveryDetails && { deliveryDetails })
        };

        const newTripRef = push(tripsRef);
        await set(newTripRef, tripRequest);

        setTripRequestId(newTripRef.key);
        console.log(`Trip request created with ID: ${newTripRef.key}`);
        // Next step would be to listen to this trip request for driver updates.
        // _listenToTripRequest(newTripRef.key);
      } catch (e) {
        console.error('--- EXCEPTION IN findDriver ---', e);
        alert(`Failed to request ride: ${e.message}`);
        setSidebarView('ride-options');
      }
    };

    if (deliveryTypes.includes(selectedRide.type)) {
      if (deliveryDetails) {
        await startSearch();
      } else {
        setIsPickupItemModalOpen(true);
      }
    } else {
      await startSearch();
    }
  };

  useEffect(() => {
    if (!tripRequestId) return;
 
    // This listener handles the transition from 'searching' to 'found'
    // and fetches the initial driver data from the `tripRequests` node.
    const tripRequestRef = ref(db, `tripRequests/${tripRequestId}`);
    const unsubscribe = onValue(tripRequestRef, async (snapshot) => {
      if (snapshot.exists()) {
        const requestData = snapshot.val();
        // Update the status from the request node. This is our primary status indicator.
        setTripStatus(requestData.status);

        // If the backend has matched this request to a trip, save the tripId
        if (requestData.tripId && tripId !== requestData.tripId) {
          setTripId(requestData.tripId);
        }

        // When status is 'found' and we don't have a driver yet
        if (requestData.status === 'found' && requestData.driverId && !assignedDriver) {
          setSidebarView('driver-found');
          
          try {
            const driverSnapshot = await get(ref(db, `drivers/${requestData.driverId}`));
            if (driverSnapshot.exists()) {
              const driverData = driverSnapshot.val();
              const vehicleData = driverData.vehicle_details || {};
              setAssignedDriver({
                id: requestData.driverId,
                name: `${driverData.first_name || ''} ${driverData.last_name || ''}`.trim(),
                photoUrl: driverData.documents?.car_profile_photo?.url || 'https://via.placeholder.com/150',
                rating: driverData.rating || 4.5, // Defaulting as per your Dart model
                vehicle: {
                  make: vehicleData.brand || 'Unknown',
                  model: vehicleData.model || 'Vehicle',
                  licensePlate: vehicleData.plateNumber || 'ABC-123',
                  color: vehicleData.color || 'Black',
                }
              });
              if (!selectedRide && rideOptions.length > 0 && requestData.vehicleClass) {
                const ride = rideOptions.find(opt => opt.type === requestData.vehicleClass);
                if (ride) setSelectedRide(ride);
              }
            } else {
              throw new Error(`Driver data not found for ID: ${requestData.driverId}`);
            }
          } catch (error) {
            console.error("Error fetching driver details:", error);
            alert("An error occurred while fetching driver details. The trip will be cancelled.");
            handleCancelSearch(); // Cancel the trip if we can't get driver details
          }
        }
      }
    });
 
    return () => unsubscribe();
  }, [tripRequestId, assignedDriver, handleCancelSearch, rideOptions, selectedRide, tripId]);

  // Effect to handle restoring an active trip from location state
  useEffect(() => {
    const restoreTrip = async () => {
      // Only run if we have an activeTrip, rideOptions are calculated, and we haven't set the driver yet
      if (activeTrip && rideOptions.length > 0 && !assignedDriver) {
        console.log("Restoring active trip UI...", activeTrip);

        // 1. Set assignedDriver from activeTrip
        if (activeTrip.driverId) {
          try {
            const driverSnapshot = await get(ref(db, `drivers/${activeTrip.driverId}`));
            if (driverSnapshot.exists()) {
              const driverData = driverSnapshot.val();
              const vehicleData = driverData.vehicle_details || {};
              setAssignedDriver({
                id: activeTrip.driverId,
                name: `${driverData.first_name || ''} ${driverData.last_name || ''}`.trim(),
                photoUrl: driverData.documents?.car_profile_photo?.url || 'https://via.placeholder.com/150',
                rating: driverData.rating || 4.5,
                vehicle: {
                  make: vehicleData.brand || 'Unknown',
                  model: vehicleData.model || 'Vehicle',
                  licensePlate: vehicleData.plateNumber || 'ABC-123',
                  color: vehicleData.color || 'Black',
                }
              });
            } else { throw new Error(`Driver data not found for ID: ${activeTrip.driverId}`); }
          } catch (error) {
            console.error("Error restoring driver details:", error);
            alert("An error occurred while restoring trip details. Please try again.");
            navigate('/');
            return;
          }
        }

        // 2. Set selectedRide and use the fare from the database
        const ride = rideOptions.find(opt => opt.type === activeTrip.vehicleClass);
        if (ride) {
          const priceFromDb = activeTrip.fare?.total;
          setSelectedRide({ ...ride, price: priceFromDb ? `₦${priceFromDb.toLocaleString()}` : ride.price });
        }
      }
    };
    restoreTrip();
  }, [activeTrip, rideOptions, assignedDriver, navigate]);

  useEffect(() => {
    if (window.google && pickup && dropoff && !map && mapRef.current) {
      const mapInstance = new window.google.maps.Map(mapRef.current, {
        center: pickup.coords,
        zoom: 12,
        disableDefaultUI: true,
        styles: mapStyles
      });
      setMap(mapInstance);

      // Initialize the polyline and store it in a ref
      routePolylineRef.current = new window.google.maps.Polyline({
        map: mapInstance,
        strokeColor: '#000000',
        strokeWeight: 5,
        strokeOpacity: 0.8,
        path: []
      });

      // Create static markers for pickup and dropoff
      pickupMarkerRef.current = new window.google.maps.Marker({
        position: pickup.coords,
        map: mapInstance,
        title: 'Pickup',
        icon: {
          url: locationpickup,
          scaledSize: new window.google.maps.Size(48, 48),
          anchor: new window.google.maps.Point(24, 48),
        }
      });

      dropoffMarkerRef.current = new window.google.maps.Marker({
        position: dropoff.coords,
        map: mapInstance,
        title: 'Dropoff',
        icon: {
          url: locationdropoff,
          scaledSize: new window.google.maps.Size(48, 48),
          anchor: new window.google.maps.Point(24, 48),
        }
      });

      // Add this part to initialize the InfoWindow
      if (!arrivalInfoWindowRef.current) {
        arrivalInfoWindowRef.current = new window.google.maps.InfoWindow({
          content: '',
          disableAutoPan: true
        });
      }
    }
  }, [pickup, dropoff, map, isLoading]);

  useEffect(() => {
    if (!map || !pickup || !routePolylineRef.current) return;

    let isMounted = true;

    // Manage marker visibilities based on status
    if (pickupMarkerRef.current && dropoffMarkerRef.current) {
      if (tripStatus === 'driving_to_destination') {
        pickupMarkerRef.current.setMap(null);
        dropoffMarkerRef.current.setMap(map);
      } else if (tripStatus === 'on_trip') {
        pickupMarkerRef.current.setMap(map);
        dropoffMarkerRef.current.setMap(null);
        if (arrivalInfoWindowRef.current) {
          arrivalInfoWindowRef.current.close();
        }
      } else {
        pickupMarkerRef.current.setMap(map);
        dropoffMarkerRef.current.setMap(map);
      }
    }

    if (assignedDriver && driverLocation) {
      const isDrivingToDest = tripStatus === 'driving_to_destination';
      
      if (isDrivingToDest) {
        // Draw polyline from pickup.coords to dropoff.coords
        fetchOsrmRoute(pickup.coords, dropoff.coords).then((result) => {
          if (!isMounted || !result) return;
          if (routePolylineRef.current) {
            routePolylineRef.current.setPath(result.path);
          }
        });

        // Query dynamic remaining duration from driver's moving position to dropoff
        fetchOsrmRoute(driverLocation, dropoff.coords).then((result) => {
          if (!isMounted || !result) return;
          setDriverEta(result.durationText);
          setRouteInfo({
            distance: { value: result.distanceMeters, text: result.distanceText },
            duration: { value: result.durationSeconds, text: result.durationText }
          });
          
          if (result.durationSeconds && arrivalInfoWindowRef.current && dropoffMarkerRef.current) {
            const arrivalTime = new Date(Date.now() + result.durationSeconds * 1000);
            const formattedTime = arrivalTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            
            arrivalInfoWindowRef.current.setContent(
              `<div class="arrival-time-label">Arriving at ${formattedTime} (${result.durationText})</div>`
            );
            arrivalInfoWindowRef.current.open(map, dropoffMarkerRef.current);
          }
        });
      } else {
        // Draw route from driverLocation to pickup.coords
        fetchOsrmRoute(driverLocation, pickup.coords).then((result) => {
          if (!isMounted || !result) return;
          if (routePolylineRef.current) {
            routePolylineRef.current.setPath(result.path);
          }
          setDriverEta(result.durationText);
          
          if (arrivalInfoWindowRef.current) {
            arrivalInfoWindowRef.current.close();
          }
        });
      }
    } else if (dropoff) {
      if (dropoffMarkerRef.current) {
        dropoffMarkerRef.current.setMap(map);
      }
      
      // No driver assigned, draw the main trip route
      fetchOsrmRoute(pickup.coords, dropoff.coords).then((result) => {
        if (!isMounted || !result) return;

        if (routePolylineRef.current) {
          routePolylineRef.current.setPath(result.path);
        }

        // Fit map bounds to show full route cleanly
        if (window.google && result.path && result.path.length > 0) {
          const bounds = new window.google.maps.LatLngBounds();
          result.path.forEach(pt => bounds.extend(pt));
          map.fitBounds(bounds, 60);
        }

        if (!routeInfo) {
          const driveDuration = { value: result.durationSeconds, text: result.durationText };
          const twoWheelerDuration = { 
            value: Math.ceil(result.durationSeconds * 0.85), 
            text: `${Math.max(1, Math.ceil((result.durationSeconds * 0.85) / 60))} min` 
          };

          setRouteInfo({
            distance: { value: result.distanceMeters, text: result.distanceText },
            duration: driveDuration
          });

          setDurations({
            DRIVING: driveDuration,
            TWO_WHEELER: twoWheelerDuration
          });

          if (result.durationSeconds && arrivalInfoWindowRef.current && dropoffMarkerRef.current) {
            const arrivalTime = new Date(Date.now() + result.durationSeconds * 1000);
            const formattedTime = arrivalTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            
            arrivalInfoWindowRef.current.setContent(`<div class="arrival-time-label">arriving at ${formattedTime}</div>`);
            arrivalInfoWindowRef.current.open(map, dropoffMarkerRef.current);
          }
        }
      });
    }

    return () => {
      isMounted = false;
    };
  }, [map, pickup, dropoff, assignedDriver, driverLocation, routeInfo, tripStatus]);

  useEffect(() => {
    if (!map) return;

    const driverStatesRef = ref(db, 'driverState');
    const driverPresencesRef = ref(db, 'driverPresence');
    const driverLocationsRef = ref(db, 'driverLocations');

    const allData = {
      states: {},
      presences: {},
      locations: {}
    };

    const updateMarkers = () => {
      const { states, presences, locations } = allData;
      const allDriverIds = new Set([
        ...Object.keys(states),
        ...Object.keys(presences),
        ...Object.keys(locations)
      ]);

      allDriverIds.forEach(driverId => {
        const state = states[driverId];
        const presence = presences[driverId];
        const location = locations[driverId];
        const existingMarker = driverMarkersRef.current[driverId];

        const isAvailable = state?.state === 'available' && presence?.online === true;

        if (isAvailable && location?.lat && location?.lng) {
          const position = { lat: location.lat, lng: location.lng };
          
          if (existingMarker) {
            existingMarker.setPosition(position);
          } else {
            const driverMarker = new window.google.maps.Marker({
              position,
              map,
              icon: {
                url: drivercar,
                scaledSize: new window.google.maps.Size(40, 40),
                anchor: new window.google.maps.Point(20, 20),
              },
              title: `Driver ${driverId}`
            });
            driverMarkersRef.current[driverId] = driverMarker;
          }
        } else {
          if (existingMarker) {
            existingMarker.setMap(null);
            delete driverMarkersRef.current[driverId];
          }
        }
      });
    };

    const statesListener = onValue(driverStatesRef, (snapshot) => { allData.states = snapshot.val() || {}; updateMarkers(); });
    const presencesListener = onValue(driverPresencesRef, (snapshot) => { allData.presences = snapshot.val() || {}; updateMarkers(); });
    const locationsListener = onValue(driverLocationsRef, (snapshot) => { allData.locations = snapshot.val() || {}; updateMarkers(); });

    return () => {
      statesListener();
      presencesListener();
      locationsListener();
      Object.values(driverMarkersRef.current).forEach(marker => marker.setMap(null));
      driverMarkersRef.current = {};
    };
  }, [map]);

  // Listen for assigned driver's location
  useEffect(() => {
    if (!assignedDriver) {
      if (driverLocation) setDriverLocation(null);
      return;
    }

    const driverLocationRef = ref(db, `driverLocations/${assignedDriver.id}`);
    const unsubscribe = onValue(driverLocationRef, (snapshot) => {
      if (snapshot.exists()) {
        const locationData = snapshot.val();
        setDriverLocation({ lat: locationData.lat, lng: locationData.lng });
      }
    });

    return () => unsubscribe();
  }, [assignedDriver, driverLocation]);

  // Manage the assigned driver's marker on the map
  useEffect(() => {
    if (map && driverLocation) {
      if (!assignedDriverMarkerRef.current) {
        assignedDriverMarkerRef.current = new window.google.maps.Marker({
          map,
          icon: {
            url: drivercar,
            scaledSize: new window.google.maps.Size(40, 40),
            anchor: new window.google.maps.Point(20, 20),
          },
          title: 'Your Driver'
        });
      }
      assignedDriverMarkerRef.current.setPosition(driverLocation);
    } else if (assignedDriverMarkerRef.current) {
      // Clean up marker if driver location is lost or trip ends
      assignedDriverMarkerRef.current.setMap(null);
      assignedDriverMarkerRef.current = null;
    }
  }, [map, driverLocation]);

  // Monitor status changes for background auto-pinging when offline or weak

  useEffect(() => {
    if (routeInfo && routeInfo.distance) {
      const distanceInKm = routeInfo.distance.value / 1000;

      const rideCategories = [
        { name: 'Economy', type: 'Economy', image: economy, base: 500, perKm: 80, perMin: 20, travelMode: 'DRIVING' },
        { name: 'First Class', type: 'First class', image: firstclass, base: 1000, perKm: 150, perMin: 40, travelMode: 'DRIVING' },
        { name: 'Business', type: 'Business', image: luxury, base: 2000, perKm: 250, perMin: 60, travelMode: 'DRIVING' },
        { name: 'Drunk Driver', type: 'Drunkdriver', image: drunkdriver, base: 1000, perKm: 160, perMin: 40, travelMode: 'DRIVING', description: 'A driver for your own car' },
        { name: 'Motorcycle', type: 'Motorcycle', image: motorcycle, base: 200, perKm: 50, perMin: 15, travelMode: 'TWO_WHEELER', description: 'Send and receive packages' },
        { name: 'Truck', type: 'Truck', image: truck, base: 3000, perKm: 300, perMin: 50, travelMode: 'DRIVING' },
        { name: 'Bicycle', type: 'Bicycle', image: bicycle, base: 150, perKm: 40, perMin: 12, travelMode: 'BICYCLING' },
      ];

      if (!isNaN(distanceInKm)) {
        const newRideOptions = rideCategories.map(cat => {
          const duration = durations[cat.travelMode] || routeInfo.duration;
          const durationInMins = Math.ceil(duration.value / 60);
          const baseNumericPrice = cat.base + (distanceInKm * cat.perKm) + (durationInMins * cat.perMin);
          const finalPrice = Math.round(baseNumericPrice / 50) * 50; // Round to nearest 50 for a cleaner price

          return {
            ...cat,
            price: `₦${finalPrice.toLocaleString()}`,
            eta: `${durationInMins} min`,
            baseNumericPrice,
          };
        });
        setRideOptions(newRideOptions);
        // Set a default selected ride, preferably 'Economy'
        if (newRideOptions.length > 0 && !activeTrip) {
          const defaultRide = newRideOptions.find(o => o.type === 'economy') || newRideOptions[0];
          setSelectedRide(defaultRide);
        }
      } else {
        setRideOptions(rideCategories.map(cat => ({...cat, price: 'N/A', eta: '...'})));
      }
    }
  }, [routeInfo, durations, activeTrip]);

  useEffect(() => {
    if (selectedRide && deliveryTypes.includes(selectedRide.type) && selectedRide.baseNumericPrice) {
      let multiplier = 1;
      if (deliveryType === 'instant') {
        multiplier = 1.5; // 50% surcharge for instant
      } else if (deliveryType === 'express') {
        multiplier = 1.2; // 20% surcharge for express
      }
  
      const newPrice = selectedRide.baseNumericPrice * multiplier;
      const finalPrice = Math.round(newPrice / 50) * 50;
      const newPriceString = `₦${finalPrice.toLocaleString()}`;
      
      // To prevent infinite loops, only update if the price is different.
      if (selectedRide.price !== newPriceString) {
        const updatedRide = { ...selectedRide, price: newPriceString };
        setSelectedRide(updatedRide);
  
        setRideOptions(prevOptions => 
          prevOptions.map(opt => 
            opt.type === selectedRide.type ? updatedRide : opt
          )
        );
      }
    }
  }, [deliveryType, selectedRide]);

  if (!pickup || !dropoff) {
    return null; // Or a loading spinner
  }

  if (isLoading) {
    return (
      <div className="go-page-wrapper" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="go-page-wrapper">
      <header className="go-header">
        <div className="go-header-left">
          <button onClick={() => navigate(-1)} className="go-header-back-btn">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>
          </button>
          <Link to="/"><img src={nitrologo} alt="9troRide Logo" className="go-header-logo" /></Link>
        </div>
        {auth.currentUser ? (
          <div style={{ position: 'relative' }} ref={profileDropdownRef}>
            <button onClick={() => setIsProfileDropdownOpen(prev => !prev)} className="go-header-profile-btn">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
              <span>{auth.riderData?.first_name || auth.currentUser.displayName?.split(' ')[0] || 'Profile'}</span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
            </button>
            {isProfileDropdownOpen && (
              <RideProfileDropdown user={auth.currentUser} rider={auth.riderData} onLogout={handleLogout} />
            )}
          </div>
        ) : (
          <Link to="/ride-login" state={{ from: location }} className="go-header-login-btn">
            Log In
          </Link>
        )}
      </header>
      <div className="go-page-container">
        <div className="go-sidebar">
          {sidebarView === 'ride-options' && (
            <>
              <div className="go-panel go-route-summary-panel">
                <div className="go-route-summary">
                  <div className="go-location-display">
                    <div className="go-timeline-icon-container">
                      <div className="go-timeline-icon start" />
                      <div className="go-timeline-connector" />
                      <div className="go-timeline-icon end" />
                    </div>
                    <div className="go-location-text-container">
                      <div className="go-location-text-group">
                        <label className="go-location-label">FROM</label>
                        <p className="go-location-address">{pickup.address}</p>
                      </div>
                      <div className="go-location-text-group">
                        <label className="go-location-label">TO</label>
                        <p className="go-location-address">{dropoff.address}</p>
                      </div>
                    </div>
                  </div>
                  {routeInfo && (
                    <div className="go-route-details">
                      <span>{routeInfo.distance.text}</span>
                      <span>•</span>
                      <span>{routeInfo.duration.text}</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="go-panel go-ride-options-panel">
                <div className="go-ride-options">
                  <h2 className="ride-options-title">Choose a ride</h2>
                  {rideOptions.map(option => (
                    <div 
                      className={`go-ride-option-card ${selectedRide?.type === option.type ? 'selected' : ''}`} 
                      key={option.name}
                      onClick={() => handleRideOptionClick(option)}
                    >
                      <img src={option.image} alt={option.name} className="ride-option-image" />
                      <div className="ride-details">
                        <h3>{option.name}</h3>
                        <p>{option.description || option.eta}</p>
                      </div>
                      <div className="ride-price">
                        <span>{option.price}</span>
                      </div>
                    </div>
                  ))}
                </div>
                 <button onClick={handleConfirmRide} className="confirm-ride-btn" disabled={!selectedRide || (deliveryTypes.includes(selectedRide.type) && !deliveryDetails)}>
                  Continue {selectedRide?.price ? `(${selectedRide.price})` : ''}
                </button>
              </div>
            </>
          )}
          {sidebarView === 'searching' && (
            <div className="go-panel searching-panel">
              <div className="spinner" />
              <h2 style={{ fontSize: '18px', fontWeight: '600', marginTop: '24px', textAlign: 'center' }}>Searching for a driver...</h2>
              <button onClick={handleCancelSearch} className="cancel-search-btn">Cancel</button>
            </div>
          )}
          {sidebarView === 'driver-found' && (
            (!assignedDriver || !selectedRide) ? (
              <div className="go-panel searching-panel">
                <div className="spinner" />
                <h2 style={{ fontSize: '18px', fontWeight: '600', marginTop: '24px', textAlign: 'center' }}>Loading trip details...</h2>
              </div>
            ) : (
              <div className={`driver-found-panel ${tripStatus === 'driver_arrived' ? 'driver-arrived' : tripStatus === 'on_trip' ? 'on-trip' : tripStatus === 'driving_to_destination' ? 'driving-to-destination' : ''}`}>
              <div className="go-modal-header">
            <h2>
              {tripStatus === 'driver_arrived' 
                ? "Your driver is here" 
                : tripStatus === 'on_trip' 
                ? "Your driver is on the way" 
                : tripStatus === 'driving_to_destination'
                ? "Estimated ride time"
                : "Your driver is on the way"}
            </h2>
            {driverEta && tripStatus !== 'driver_arrived' && (
              <div className="driver-eta-header">
                {tripStatus === 'driving_to_destination' ? `${driverEta} remaining` : `${driverEta} arriving`}
              </div>
            )}
              </div>
              <div className="go-modal-body">
                <div className="driver-details-card">
                  <img 
                    src={assignedDriver.photoUrl} 
                    alt={assignedDriver.name} 
                    className="driver-photo clickable-driver-photo" 
                    onClick={() => setIsDriverModalOpen(true)}
                    title="Click to view driver details"
                  />
                  <div className="driver-info">
                    <h3>{assignedDriver.name}</h3>
                    <div className="driver-rating">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>
                      <span>{assignedDriver.rating.toFixed(1)}</span>
                    </div>
                  </div>
                  <div className="driver-actions">
                    {tripStatus === 'on_trip' && tripId && (
                      <button className="driver-chat-btn" aria-label="Chat with driver" onClick={() => setIsChatOpen(true)}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
                      </button>
                    )}
                  </div>
                </div>

                <div className="vehicle-details-card">
                  <div className="vehicle-info">
                    <div className="vehicle-plate">{assignedDriver.vehicle.licensePlate}</div>
                    <div className="vehicle-model">{`${assignedDriver.vehicle.color} ${assignedDriver.vehicle.make} ${assignedDriver.vehicle.model}`}</div>
                  </div>
                  <img src={selectedRide.image} alt={selectedRide.name} className="vehicle-image" />
                </div>

                <div className="trip-summary-card">
                  <div className="trip-route-line">
                    <div className="trip-route-item">
                      <div className="route-icon pickup-icon" />
                      <div className="route-address">{pickup.address}</div>
                    </div>
                    <div className="trip-route-item">
                      <div className="route-icon dropoff-icon" />
                      <div className="route-address">{dropoff.address}</div>
                    </div>
                  </div>
                  <div className="trip-fare-details">
                    <div className="fare-label">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"></line><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path></svg>
                      <span>Total Fare</span>
                    </div>
                    <span className="fare-amount">{selectedRide.price}</span>
                  </div>
                </div>
              </div>
              <div className="go-modal-footer"><button onClick={handleCancelSearch} className="cancel-trip-btn">Cancel Trip</button></div>
              </div>
            )
          )}
        </div>
        {isChatOpen && assignedDriver && tripId && (
          <Chat // This needs to use the correct tripId for the /trips node
            tripId={tripId}
            user={auth.currentUser}
            participant={{ id: assignedDriver.id, name: assignedDriver.name, photoUrl: assignedDriver.photoUrl }}
            onClose={() => setIsChatOpen(false)}
          />
        )}
        <div className="go-map-container" ref={mapRef}></div>
        <div className="map-zoom-controls">
          <button onClick={handleZoomIn} aria-label="Zoom in">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
          </button>
          <button onClick={handleZoomOut} aria-label="Zoom out">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line></svg>
          </button>
        </div>
      </div>
      {/* Conditionally render PickupItemModal */}
      {isPickupItemModalOpen && (
        <PickupItemModal
          vehicle={selectedRide}
          onClose={handleCancelDelivery}
          onConfirm={handleConfirmDelivery}
          deliveryType={deliveryType}
          onDeliveryTypeChange={setDeliveryType}
          pickup={pickup}
          dropoff={dropoff}
        />
      )}

      {/* Driver Details Modal */}
      {isDriverModalOpen && assignedDriver && (
        <div className="custom-driver-modal-overlay" onClick={() => setIsDriverModalOpen(false)}>
          <div className="custom-driver-modal-card" onClick={(e) => e.stopPropagation()}>
            <button className="custom-modal-close-btn" onClick={() => setIsDriverModalOpen(false)} aria-label="Close modal">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
            </button>
            <div className="custom-modal-header">
              <h2>Driver Profile</h2>
            </div>
            <div className="custom-modal-body">
              <div className="custom-driver-hero">
                <img src={assignedDriver.photoUrl} alt={assignedDriver.name} className="custom-driver-avatar" />
                <h3>{assignedDriver.name}</h3>
                <div className="custom-driver-rating-badge">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>
                  <span>{assignedDriver.rating.toFixed(1)} Rating</span>
                </div>
              </div>
              
              <div className="custom-details-section">
                <div className="detail-item">
                  <span className="detail-label">Vehicle Info</span>
                  <span className="detail-value">{`${assignedDriver.vehicle.color} ${assignedDriver.vehicle.make} ${assignedDriver.vehicle.model}`}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">License Plate</span>
                  <span className="detail-value-plate">{assignedDriver.vehicle.licensePlate}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Phone Number</span>
                  <span className="detail-value">{assignedDriver.phone || "+234 812 345 6789"}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Verification status</span>
                  <span className="detail-value-status verified">✓ Verified 9tro Driver</span>
                </div>
              </div>

              <div className="custom-modal-actions">
                <button className="custom-report-btn" onClick={() => { setIsDriverModalOpen(false); setIsReportModalOpen(true); }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>
                  Report Driver
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Report Driver Modal */}
      {isReportModalOpen && assignedDriver && (
        <div className="custom-driver-modal-overlay" onClick={() => setIsReportModalOpen(false)}>
          <div className="custom-driver-modal-card" onClick={(e) => e.stopPropagation()}>
            <button className="custom-modal-back-btn" onClick={() => { setIsReportModalOpen(false); setIsDriverModalOpen(true); }} aria-label="Go back">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>
            </button>
            <div className="custom-modal-header">
              <h2>Report Driver</h2>
            </div>
            
            {reportSuccess ? (
              <div className="custom-report-success-state">
                <div className="success-icon-ring">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                </div>
                <h3>Report Submitted</h3>
                <p>Thank you. Your feedback helps keep the 9tro community safe. We will investigate this incident.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmitReport} className="custom-report-form">
                <p className="form-sub-label">Select the reason for reporting {assignedDriver.name}:</p>
                <div className="reason-options">
                  {[
                    'Reckless Driving',
                    'Drunk or Distracted Driver',
                    'Unprofessional Behavior',
                    'Vehicle mismatch description',
                    'Safety concern',
                    'Other issues'
                  ].map((reason) => (
                    <label key={reason} className={`reason-pill ${reportReason === reason ? 'active' : ''}`}>
                      <input 
                        type="radio" 
                        name="reportReason" 
                        value={reason} 
                        checked={reportReason === reason} 
                        onChange={() => setReportReason(reason)}
                        className="hidden-radio"
                      />
                      <span>{reason}</span>
                    </label>
                  ))}
                </div>

                <div className="comments-section">
                  <label htmlFor="report-comments">Additional details (optional):</label>
                  <textarea 
                    id="report-comments"
                    rows="3" 
                    value={reportComments} 
                    onChange={(e) => setReportComments(e.target.value)}
                    placeholder="Provide details about what happened..."
                  />
                </div>

                <div className="form-actions">
                  <button type="submit" className="custom-submit-report-btn" disabled={isReporting}>
                    {isReporting ? (
                      <>
                        <svg className="spinning-loader-small" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.15)"></circle>
                          <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round"></path>
                        </svg>
                        Submitting...
                      </>
                    ) : 'Submit Report'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Go;